import React, { useEffect, useState, useRef } from 'react';
import { MegaphoneSimple, WarningCircle, ArrowRight } from '@phosphor-icons/react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@t-one-internal/design-system';
import apiClient from '../../api/apiClient';

interface BannerNotification {
  id: number;
  title: string;
  category: string;
  priority: string;
  is_read: boolean;
  is_dismissed: boolean;
  content_body?: string;
  bullet_point?: string;
  custom_html_content?: string;
  created_at: string;
}

interface BannerResponse {
  message: string;
  data: BannerNotification | null;
}

// Storage key for permanent dismissals only
const DISMISSED_KEY = 'dismissed_notification_banners';

export const NotificationBanner: React.FC = () => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState<BannerNotification | null>(null);
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  // Track temporarily hidden notifications (session-level, resets on refresh)
  const tempHiddenIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    fetchBannerNotification();
    const interval = setInterval(fetchBannerNotification, 120000);
    return () => clearInterval(interval);
  }, []);

  // Listen for notification deletion events from other packages (e.g. workforce)
  useEffect(() => {
    const handler = () => fetchBannerNotification();
    window.addEventListener('notification-deleted', handler);
    return () => window.removeEventListener('notification-deleted', handler);
  }, []);

  const fetchBannerNotification = async () => {
    try {
      // Build query params with excluded IDs (both permanent and temporary)
      const permanentDismissed = getPermanentlyDismissed();
      const tempHidden = Array.from(tempHiddenIdsRef.current);
      const allExcluded = [...permanentDismissed, ...tempHidden];
      
      const params = allExcluded.length > 0 
        ? `?exclude_ids=${allExcluded.join(',')}` 
        : '';
      
      const response = await apiClient.get<BannerResponse>(`/user-notifications/banner${params}`);
      const banner = response.data.data;
      
      if (banner) {
        setNotification(banner);
        setVisible(true);
      } else {
        setNotification(null);
        setVisible(false);
      }
    } catch (error) {
      console.error('Failed to fetch banner notification:', error);
    }
  };

  const getPermanentlyDismissed = (): number[] => {
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const handleDismissClick = () => {
    setDialogOpen(true);
  };

  const confirmDismiss = async () => {
    if (!notification) return;

    const notificationId = notification.id;

    if (dontShowAgain) {
      // Permanent dismiss - call API and store in localStorage
      try {
        await apiClient.put(`/user-notifications/${notificationId}/dismiss`);
        const dismissed = getPermanentlyDismissed();
        dismissed.push(notificationId);
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
      } catch (error) {
        console.error('Failed to dismiss banner permanently:', error);
      }
    } else {
      // Temporary hide - add to session-level ref (won't persist on refresh)
      tempHiddenIdsRef.current.add(notificationId);
    }

    setDialogOpen(false);
    setDontShowAgain(false);
    setVisible(false);
    setNotification(null);
    
    // Fetch the next banner notification immediately
    setTimeout(() => {
      fetchBannerNotification();
    }, 100);
  };

  const cancelDismiss = () => {
    setDialogOpen(false);
    setDontShowAgain(false);
  };

  const handleView = () => {
    if (notification) {
      // Navigate to notifications page with this notification ID to auto-select it
      navigate(`/workforce/notifications?id=${notification.id}`);
    }
  };

  // Get category label
  const getBadgeLabel = () => {
    switch (notification?.category) {
      case 'maintenance':
        return 'Maintenance';
      case 'product_announcement':
        return 'Announcement';
      default:
        return 'Notification';
    }
  };

  if (!notification || !visible) return null;

  const contentPreview = notification.content_body 
    ? notification.content_body.split('\n')[0].replace(/^[-•]\s*/, '').substring(0, 80)
    : '';

  return (
    <>
      {/* Banner - matches ImpersonationBanner style */}
      <div
        className="h-9 px-4 flex items-center justify-between text-sm bg-gradient-to-r from-amber-600 to-amber-500 text-white"
        style={{ zIndex: 9998 }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Icon */}
          {notification.category === 'maintenance' ? (
            <WarningCircle className="w-4 h-4 flex-shrink-0" weight="fill" />
          ) : (
            <MegaphoneSimple className="w-4 h-4 flex-shrink-0" weight="fill" />
          )}

          {/* Category label */}
          <span className="font-medium">
            {getBadgeLabel()}
          </span>

          {/* Divider */}
          <div className="hidden sm:block h-3.5 w-px bg-white/30" />

          {/* Title */}
          <span className="font-medium truncate">
            {notification.title}
          </span>

          {/* Content preview - hidden on small screens */}
          {contentPreview && (
            <>
              <div className="hidden sm:block h-3.5 w-px bg-white/30" />
              <span className="hidden sm:block text-white/80 truncate max-w-[300px]">
                {contentPreview}
              </span>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleView}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            View
            <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={handleDismissClick}
            className="flex items-center justify-center h-6 w-6 rounded hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Dismiss Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Hide Notification</DialogTitle>
            <DialogDescription>
              You can hide this notification for now or choose not to see it again.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              />
              <span className="text-sm text-muted-foreground">
                Don't show this again
              </span>
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={cancelDismiss}>
              Cancel
            </Button>
            <Button onClick={confirmDismiss}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBanner;
